import Breadcrumb from '@/components/Breadcrumb';
import Meta from '@/components/Meta';
import ProductDetailImage from '@/components/ProductDetailImage';
import ProductOptions from '@/components/ProductOptions';
import ProductSlider from '@/components/ProductSlider';
import MainLayout from '@/components/layouts/MainLayout';
import { FB_APP_ID, RVI_LS_PROPERTY_NAME, dataLayer } from '@/constants';
import { Environments, QueryKeys } from '@/enums';
import { getReactQueryClient } from '@/helpers/get-react-query-client';
import { useCrossCategoryProduct, useProduct, useRecentlyViewedProducts, useRecommendedProduct, useUpdateInfoGraphic } from '@/hooks/products';
import { useReactQuerySession } from '@/hooks/reactQuerySession';
import { IconArrowDown } from '@/icons/IconArrowDown';
import IComponentProps from '@/interfaces/componentProps';
import { IProductDetails, IProductModel } from '@/interfaces/product';
import { getCrossCategorySuggestionsList, getProduct, getProducts, getRecommendedProductList } from '@/services/product.service';
import { stripHTML } from '@/utils/helper';
import { dehydrate } from '@tanstack/react-query';
import { GetStaticPaths, GetStaticProps, GetStaticPropsContext } from 'next';
import {
  EmailShareButton,
  FacebookMessengerShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from 'next-share';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import React, { useEffect, useState } from 'react';

export const getStaticPaths: GetStaticPaths = async () => {
  try {
    let response = await getProducts(10, 1);
    if (response.PageData.length) {
      switch (process.env.NEXT_PUBLIC_APP_ENVIRONMENT) {
        case Environments.Production:
          break;
        case Environments.Staging:
          response.PageData = [response.PageData[0]];
          break;
        case Environments.Development:
          response.PageData = [response.PageData[0]];
          break;
        case Environments.Local:
          response.PageData = [response.PageData[0]];
          break;
      }
    }
    return {
      paths: response
        ? response.PageData?.map((product: IProductModel) => {
            return {
              params: {
                seName: product.SeName,
              },
            };
          })
        : [],
      fallback: 'blocking',
    };
  } catch (e: any) {
    return {
      paths: [],
      fallback: false,
    };
  }
};

export const getStaticProps: GetStaticProps = async (context: GetStaticPropsContext) => {
  let typedParams = context.params as ParsedUrlQuery & { seName: string };
  const { queryClient } = await getReactQueryClient();
  try {
    let queryCalls = [
      queryClient.prefetchQuery([typedParams.seName], async () => {
        let response = {};
        try {
          response = await getProduct(typedParams.seName);
        } catch (error: any) {
          if (error.status === 404) {
            return 404;
          }
        }
        return response;
      }),
      queryClient.prefetchQuery([QueryKeys.recommendedProducts + typedParams.seName], () => {
        return getRecommendedProductList(typedParams.seName).then((data) => {
          return data;
        });
      }),
      queryClient.prefetchQuery([QueryKeys.crossCategorySuggestions + typedParams.seName], () => {
        return getCrossCategorySuggestionsList(typedParams.seName).then((data) => {
          return data;
        });
      }),
    ];
    await Promise.all(queryCalls);
    let dehydratedState = dehydrate(queryClient);
    let productQuery = dehydratedState.queries[2];
    if (productQuery.queryKey[0] === typedParams.seName && productQuery.state.data === 404) {
      return { notFound: true, revalidate: 60 };
    }
  } catch (e: any) {
    console.log(e, 'promise all error');
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
    revalidate: 120,
  };
};

const ProductSection = ({ title, children, className, icons, id }: IComponentProps & { title: string; children: any; icons?: any; id: string }) => {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [showMoreOrLessVisible, setShowMoreOrLessVisible] = useState<boolean>(false);
  const [showMore, setShowMore] = useState<boolean>(true);

  useEffect(() => {
    let elementHeight = document.getElementById(id)?.scrollHeight;
    console.log(elementHeight);
    if (elementHeight && elementHeight > 192) {
      setShowMoreOrLessVisible(true);
    } else {
      setShowMoreOrLessVisible(false);
    }
  }, []);

  return (
    <section className={`group relative ${className} ${isOpen ? 'open' : ''}`}>
      <div onClick={() => setIsOpen(!isOpen)} className="mb-2 flex cursor-pointer text-h5 font-medium">
        <div className="relative mr-2 h-6 w-6 self-center">{<Image unoptimized={true} fill quality={100} src={icons} alt="icon" />}</div>
        {title}
        <IconArrowDown className="ml-auto w-5 transition-all duration-[50ms] group-[.open]:rotate-180" />
      </div>
      <div className={`hidden pl-2.5 group-[.open]:block ${!showMore ? 'h-auto' : 'max-h-64 overflow-hidden'}`}>{children}</div>
      {showMoreOrLessVisible && (
        <span
          className={`${showMore ? 'absolute' : ''} inset-x-0 bottom-0 hidden cursor-pointer bg-white text-center underline group-[.open]:block ${
            showMore ? 'show-more-less pt-12' : 'pt-4'
          }`}
          onClick={() => setShowMore(!showMore)}
        >
          {showMore ? 'Read More' : 'Read Less'}
        </span>
      )}
      <style jsx>{`
        .show-more-less {
          background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.8) 20%, rgba(255, 255, 255, 1) 75%);
        }
      `}</style>
    </section>
  );
};

const ProductDetail = () => {
  const router = useRouter();
  const seName = router.query.seName as string;
  const [shareLink, setShareLink] = useState<string>('');
  const { data: product } = useProduct(seName, false);
  const [arrayOfProductIds, setArrayOfProductIds] = useState<string[]>([]);
  const { data: recentlyViewedProducts } = useRecentlyViewedProducts(arrayOfProductIds);
  const { mutate: mutateInfoGraphic } = useUpdateInfoGraphic(seName, false);
  const [isRefetchOnMount, setIsRefetchOnMount] = useState<boolean>(true);
  const { data: recommendedProducts } = useRecommendedProduct(seName);
  const { data: crossCategoryProducts } = useCrossCategoryProduct(seName);

  useEffect(() => {
    if (router.isReady) {
      let stringifiedRVIs = localStorage.getItem(RVI_LS_PROPERTY_NAME);
      if (stringifiedRVIs) {
        try {
          let tempArrayOfProductIds = JSON.parse(stringifiedRVIs);
          if (Array.isArray(tempArrayOfProductIds)) {
            //tempArrayOfProductIds is a valid array
            console.log('setting array of products');
            setArrayOfProductIds(tempArrayOfProductIds as string[]);
          }
        } catch (e) {
          console.log('json parse error', e);
        }
      }
      setShareLink(window.location.href);
      mutateInfoGraphic(seName);
    }
  }, [router.asPath, router.isReady]);
  useEffect(() => {
    if (router.isReady && product && isRefetchOnMount) {
      setIsRefetchOnMount(false);
      let obj: { [key: string]: string } = {};

      if (product?.CategoryName) {
        let splittedCategories = product?.CategoryName.split(',');
        splittedCategories.forEach((category, categoryIndex) => {
          obj['item_category' + (categoryIndex ? categoryIndex : '')] = category;
        });
      }
      if (!product?.IsGiftVoucher && product?.Gender) {
        obj['gender'] = product?.Gender;
      }

      const itemArray = [
        {
          item_id: product?.SeName,
          item_name: product?.Name,
          item_sku: product?.Sku,
          item_brand: product?.ProductBrand?.Name,
          price: product?.ProductPrice?.Price,
          quantity: product?.StockQuantity,
          item_variant: product?.Variations?.map((variation) => variation?.VariantSku),
          color: product?.AvailableColors?.map((color) => color?.ColorName),
          size: product?.Variations?.map((variation) => variation?.ProductAttributeValue),
          ...obj,
        },
      ];
      dataLayer.push({ ecommerce: null });
      dataLayer.push({
        event: 'select_item',
        ecommerce: {
          items: itemArray,
        },
      });
      dataLayer.push({ ecommerce: null });
      dataLayer.push({
        event: 'view_item',
        ecommerce: {
          items: itemArray,
        },
      });
    }
  }, [router.isReady, product]);

  useEffect(() => {
    setIsRefetchOnMount(true);
  }, [router.asPath]);

  const completeLookSlides = product?.CompleteTheLooks?.map((slide, index) => ({
    id: slide.Id,
    FeaturedImageUrl: slide.FeaturedImageUrl,
    name: slide.Name,
    options: slide.Colors,
    productPrice: slide.ProductPrice,
    seName: slide.SeName,
    altText: slide.AltText,
  }));

  const relatedProductSlides = product?.YouMayAlsoLikes?.map((slide, index) => ({
    id: slide.Id,
    FeaturedImageUrl: slide.FeaturedImageUrl,
    name: slide.Name,
    options: slide.Colors,
    productPrice: slide.ProductPrice,
    seName: slide.SeName,
    altText: slide.AltText,
  }));
  const recommendedProductSlides = recommendedProducts?.map((slide, index) => ({
    id: slide.Id,
    FeaturedImageUrl: slide.FeaturedImageUrl,
    name: slide.Name,
    options: slide.Colors,
    productPrice: slide.ProductPrice,
    seName: slide.SeName,
    altText: slide.AltText,
  }));

  const otherRelatedProductSlides = product?.OthersAlsoLikes?.map((slide, index) => ({
    id: slide.Id,
    FeaturedImageUrl: slide.FeaturedImageUrl,
    name: slide.Name,
    options: slide.Colors,
    productPrice: slide.ProductPrice,
    seName: slide.SeName,
    altText: slide.AltText,
  }));
  const crossCategorySlides = crossCategoryProducts?.map((slide, index) => ({
    id: slide.Id,
    FeaturedImageUrl: slide.FeaturedImageUrl,
    name: slide.Name,
    options: slide.Colors,
    productPrice: slide.ProductPrice,
    seName: slide.SeName,
    altText: slide.AltText,
  }));

  const recentlyViewedProductSlides = recentlyViewedProducts?.map((slide, index) => ({
    id: slide.Id,
    FeaturedImageUrl: slide.FeaturedImageUrl,
    name: slide.Name,
    options: slide.Colors,
    productPrice: slide.ProductPrice,
    seName: slide.SeName,
    altText: slide.AltText,
  }));
  const accordionImages = ['https://storage.apex4u.com/Materials.png', 'https://storage.apex4u.com/Shipping.png'];

  const getFeaturedImage = (productImageList: IProductDetails['ProductImageList']) => {
    return productImageList.find((data) => {
      if (data.IsFeaturedImage === true) {
        return data;
      }
    });
  };

  return (
    <>
      <Meta
        title={product?.Name}
        MetaTitle={product?.MetaTitle}
        description={product?.MetaDescription}
        keywords={product?.MetaKeywords}
        ImageUrl={product?.ProductImageList ? getFeaturedImage(product?.ProductImageList)?.Url : ''}
        altTag={product?.ProductImageList ? getFeaturedImage(product?.ProductImageList)?.TitleAttribute : ''}
        product={product}
        breadcrumbs={product?.Breadcrumbs}
      />
      <div className="container">
        <div>
          <div>
            <div className="flex justify-center">
              <div className="lg:-mx-13 w-full justify-center lg:flex lg:px-11">
                <div className="flex grow flex-col lg:min-w-[40rem]">
                  {product?.Breadcrumbs && <Breadcrumb className="" bred={product?.Breadcrumbs} />}
                  <ProductDetailImage productDetail={product} />
                </div>
                <div className="mt-12 lg:w-full lg:min-w-[40rem] lg:max-w-[40rem] lg:pl-[60px]">
                  <ProductOptions quickView={false} product={product || ({} as IProductDetails)} productInfoGraphic={product?.ProductViewed} />
                  {(stripHTML(product?.ShortDescription) || stripHTML(product?.FullDescription)) && (
                    <ProductSection className="description my-5" icons="/images/description.png" title="Description" id="description">
                      <div id="description">
                        <div className="mx-6 text-justify md:mx-0 md:ml-6" dangerouslySetInnerHTML={{ __html: product?.ShortDescription || '' }} />
                        <div className="mx-6 text-justify md:mx-0 md:ml-6" dangerouslySetInnerHTML={{ __html: product?.FullDescription || '' }} />
                      </div>
                    </ProductSection>
                  )}
                  {product?.ProductSpecifications &&
                    product?.ProductSpecifications?.length > 0 &&
                    product?.ProductSpecifications?.map((specification, index) => (
                      <div key={index}>
                        {stripHTML(specification?.ValueRaw) && (
                          <ProductSection
                            id={`${specification?.SpecificationAttributeName}-${index}`}
                            className={`my-5 ${specification?.SpecificationAttributeName.toLowerCase().replaceAll(' ', '-')}`}
                            icons={accordionImages[index] || '/images/description.png'}
                            key={specification?.SpecificationAttributeName}
                            title={specification?.SpecificationAttributeName}
                          >
                            <div
                              id={`${specification?.SpecificationAttributeName}-${index}`}
                              key={specification?.SpecificationAttributeName}
                              className=" mx-6 text-justify md:mx-0 md:ml-6"
                              dangerouslySetInnerHTML={{ __html: specification?.ValueRaw }}
                            />
                          </ProductSection>
                        )}
                      </div>
                    ))}
                  <div>
                    {(stripHTML(product?.Disclaimer)) && (
                      <ProductSection className="disclaimer my-5" icons="/images/disclaimer.png" title="Disclaimer" id="disclaimer">
                        <div id="disclaimer">
                          <div className="mx-6 text-justify md:mx-0 md:ml-6" dangerouslySetInnerHTML={{ __html: product?.Disclaimer || '' }} />
                        </div>
                      </ProductSection>
                    )}
                  </div>

                  <div className="mt-5 flex items-center justify-center">
                    <FacebookShareButton url={`${shareLink}`} windowWidth={800} windowHeight={700}>
                      <div className=" mr-1.5 flex aspect-square w-[32px] cursor-pointer items-center justify-center rounded-md bg-[#4267B2] transition duration-500 hover:-translate-y-2 hover:opacity-80 ">
                        <div className="relative aspect-square w-[16px]">
                          <Image unoptimized={true} fill quality={100} src="/icons/facebook.svg" alt="facebook"></Image>
                        </div>
                      </div>
                    </FacebookShareButton>
                    <FacebookMessengerShareButton url={`${shareLink}`} title={product?.Name} appId={FB_APP_ID} windowWidth={800} windowHeight={700}>
                      <div className=" mx-1.5 flex aspect-square w-[32px] cursor-pointer items-center justify-center rounded-md bg-[#448AFF] transition duration-500 hover:-translate-y-2 hover:opacity-80">
                        <div className="relative aspect-square w-[16px]">
                          <Image unoptimized={true} fill quality={100} src="/icons/messenger.svg" alt="messenger"></Image>
                        </div>
                      </div>
                    </FacebookMessengerShareButton>

                    <LinkedinShareButton url={`${shareLink}`} title={product?.Name} windowWidth={800} windowHeight={700}>
                      <div className=" mx-1.5 flex aspect-square w-[32px] cursor-pointer items-center justify-center rounded-md bg-[#448AFF] transition duration-500 hover:-translate-y-2 hover:opacity-80">
                        <div className="relative aspect-square w-[16px]">
                          <Image unoptimized={true} fill quality={100} src="/icons/linkedin.svg" alt="linkedin"></Image>
                        </div>
                      </div>
                    </LinkedinShareButton>
                    <TwitterShareButton url={`${shareLink}`} title={product?.Name} windowWidth={800} windowHeight={700}>
                      <div className=" mx-1.5 flex aspect-square w-[32px] cursor-pointer items-center justify-center rounded-md bg-[#55acee] transition duration-500 hover:-translate-y-2 hover:opacity-80 ">
                        <div className="relative aspect-square w-[16px]">
                          <Image unoptimized={true} fill quality={100} src="/icons/twitter.svg" alt="twitter"></Image>
                        </div>
                      </div>
                    </TwitterShareButton>
                    <WhatsappShareButton url={`${shareLink}`} title={product?.Name} windowWidth={800} windowHeight={700}>
                      <div className=" mx-1.5 flex aspect-square w-[32px] cursor-pointer items-center justify-center rounded-md bg-[#25d366] transition duration-500 hover:-translate-y-2 hover:opacity-80 ">
                        <div className="relative aspect-square w-[16px]">
                          <Image unoptimized={true} fill quality={100} src="/icons/whatsapp.svg" alt="whatsapp"></Image>
                        </div>
                      </div>
                    </WhatsappShareButton>
                    {/* <TelegramShareButton url={`${shareLink}`} title={product?.Name} windowWidth={800} windowHeight={700}>
                      <div className=" mx-1.5 flex aspect-square w-[32px] cursor-pointer items-center justify-center rounded-md bg-[#0088cc] transition duration-500 hover:-translate-y-2 hover:opacity-80 ">
                        <div className="relative aspect-square w-[16px]">
                          <Image unoptimized={true} fill quality={100} src="/icons/telegram.svg" alt="telegram"></Image>
                        </div>
                      </div>
                    </TelegramShareButton> */}
                    <EmailShareButton url={`${shareLink}`} windowWidth={800} windowHeight={700}>
                      <div className=" mx-1.5 flex aspect-square w-[32px] cursor-pointer items-center justify-center rounded-md bg-[#645EA4] transition duration-500 hover:-translate-y-2 hover:opacity-80">
                        <div className="relative aspect-square w-[16px]">
                          <Image unoptimized={true} fill quality={100} src="/icons/email1.svg" alt="email"></Image>
                        </div>
                      </div>
                    </EmailShareButton>
                    <div
                      className=" mx-1.5 flex aspect-square h-[32px] w-[32px] cursor-pointer items-center justify-center rounded-md bg-[#222222] transition duration-500 hover:-translate-y-2 hover:opacity-80"
                      id="print"
                      onClick={() => {
                        window?.print();
                      }}
                    >
                      <div className="relative aspect-square w-[16px]">
                        <Image unoptimized={true} fill quality={100} src="/icons/print1.svg" alt="print"></Image>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-hidden">
            <section className="content-section">
              {!!completeLookSlides?.length && (
                <div className="mt-10">
                  <p className="product-carousel__heading ">
                    <span className="product-carousel__title">Complete the look</span>
                  </p>
                  <ProductSlider navigation={true} className="mt-3" sliderType="complete-the-look" products={completeLookSlides} />
                </div>
              )}
              {!!crossCategorySlides?.length && (
                <div className="mt-8">
                  <p className="product-carousel__heading ">
                    <span className="product-carousel__title">People who bought this also buy these</span>
                  </p>
                  <ProductSlider navigation={true} className="mt-3" sliderType="cross-category-suggestions" products={crossCategorySlides} />
                </div>
              )}
              {!!relatedProductSlides?.length && (
                <div className="mt-8">
                  <p className="product-carousel__heading ">
                    <span className="product-carousel__title">You may also like</span>
                  </p>
                  <ProductSlider navigation={true} className="mt-3" sliderType="you-may-also-like" products={relatedProductSlides} />
                </div>
              )}
              {!!recommendedProductSlides?.length && (
                <div className="mt-8">
                  <p className="product-carousel__heading ">
                    <span className="product-carousel__title">Recommended products</span>
                  </p>
                  <ProductSlider navigation={true} className="mt-3" sliderType="recommended-product" products={recommendedProductSlides} />
                </div>
              )}
              {!!recentlyViewedProductSlides?.length && (
                <div className="mt-8">
                  <p className="product-carousel__heading ">
                    <span className="product-carousel__title">Recently viewed items</span>
                  </p>
                  <ProductSlider navigation={true} className="mt-3" sliderType="recently-viewed-items" products={recentlyViewedProductSlides} />
                </div>
              )}
              {!!otherRelatedProductSlides?.length && (
                <div className="mt-8">
                  <p className="product-carousel__heading ">
                    <span className="product-carousel__title">Trending Now</span>
                  </p>
                  <ProductSlider navigation={true} className="mt-3" sliderType="trending-now" products={otherRelatedProductSlides} />
                </div>
              )}
            </section>
          </div>
        </div>
        <style jsx>{`
          .product-carousel__heading {
            position: relative;
            margin: 0 0 20px;
            line-height: 23px;
            text-align: center;
          }
          .product-carousel__heading:before {
            position: absolute;
            margin-left: -3rem;
            margin-right: -3rem;
            top: 14px;
            right: 0;
            left: 0;
            height: 1px;
            content: '';
            background: #000;
          }
          .product-carousel__heading:after {
            position: absolute;
            top: 14px;
            right: 0;
            left: 0;
            height: 1px;
            content: '';
            padding-top: 24px;
            border-left: 1px solid black;
            border-right: 1px solid black;
            margin-left: -3rem;
            margin-right: -3rem;
          }
          .product-carousel__title {
            letter-spacing: 0.2em;
            text-transform: uppercase;
            position: relative;
            display: inline-block;
            padding: 0 20px;
            font-size: 16px;
            line-height: 28px;
            background: #fff;
            font-weight: 600;
          }
        `}</style>
      </div>
    </>
  );
};

ProductDetail.Layout = MainLayout;

export default ProductDetail;
